import { ReactElement, useState, useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import { useSessions } from '../../hooks/useSessions';
import { useRidersById } from '../../hooks/useRiders';
import { getSessionCalcData } from '../../api/sessions';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Table from '../../components/Table';
import { SessionStatistics, AggregateStatistics, CalculationMethod } from '../../types/Statistics';

// Interface for storing detailed lap difference data for export
interface LapDifferenceRecord {
  sessionDate: string;
  rider: string;
  sessionMeanCdA: number;
  lapCdA: number;
  difference: number;
}
import { SessionAnalysisTypeUtils } from '../../types/SessionAnalysisType';
import {
  calculateSessionStatistics,
  calculateAggregateStatistics,
  extractConfigACdAValues,
  calculateLapCdAValues,
  calculateMean,
} from '../../utils/statisticsUtils';
import { executeExportHtml } from '../../utils/printUtils';

// Histogram bucket configuration
const CV_BUCKET_SIZE = 0.2; // 0.2% increments
const CV_MAX = 2.4; // up to 2.4%
const TE_BUCKET_SIZE = 0.0002; // 0.0002 increments
const TE_MAX = 0.0024; // up to 0.0024

/**
 * Create histogram buckets from session statistics
 */
function createHistogramData(
  values: number[],
  bucketSize: number,
  maxValue: number
): { labels: string[]; counts: number[] } {
  const numBuckets = Math.ceil(maxValue / bucketSize);
  const counts = new Array(numBuckets + 1).fill(0); // +1 for overflow bucket
  const labels: string[] = [];

  // Create labels for each bucket
  for (let i = 0; i < numBuckets; i++) {
    const start = i * bucketSize;
    const end = (i + 1) * bucketSize;
    labels.push(`${start.toFixed(bucketSize < 0.01 ? 4 : 1)}-${end.toFixed(bucketSize < 0.01 ? 4 : 1)}`);
  }
  labels.push(`>${maxValue.toFixed(bucketSize < 0.01 ? 4 : 1)}`);

  // Count values in each bucket
  for (const value of values) {
    if (value >= maxValue) {
      counts[numBuckets]++; // overflow bucket
    } else {
      const bucketIndex = Math.floor(value / bucketSize);
      counts[Math.min(bucketIndex, numBuckets)]++;
    }
  }

  return { labels, counts };
}

/**
 * CV Histogram Component
 */
function CVHistogram({ sessionStatistics }: { sessionStatistics: SessionStatistics[] }): ReactElement {
  const chartData = useMemo(() => {
    const cvValues = sessionStatistics.map((s) => s.cv);
    const { labels, counts } = createHistogramData(cvValues, CV_BUCKET_SIZE, CV_MAX);

    const options: ApexOptions = {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'Poppins, sans-serif',
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '80%',
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: ['#03365D'],
        },
      },
      xaxis: {
        categories: labels,
        title: {
          text: 'CV (%)',
          style: { color: '#03365D', fontWeight: 600 },
        },
        labels: {
          style: { colors: '#03365D' },
          rotate: -45,
          rotateAlways: true,
        },
      },
      yaxis: {
        title: {
          text: 'Session Count',
          style: { color: '#03365D', fontWeight: 600 },
        },
        labels: {
          style: { colors: '#03365D' },
          formatter: (val: number) => Math.round(val).toString(),
        },
      },
      colors: ['#1879B9'],
      grid: {
        borderColor: '#e0e0e0',
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} session${val !== 1 ? 's' : ''}`,
        },
      },
    };

    const series = [{ name: 'Sessions', data: counts }];

    return { options, series };
  }, [sessionStatistics]);

  return <Chart options={chartData.options} series={chartData.series} type="bar" height={300} />;
}

/**
 * TE Histogram Component
 */
function TEHistogram({ sessionStatistics }: { sessionStatistics: SessionStatistics[] }): ReactElement {
  const chartData = useMemo(() => {
    const teValues = sessionStatistics.map((s) => s.te);
    const { labels, counts } = createHistogramData(teValues, TE_BUCKET_SIZE, TE_MAX);

    const options: ApexOptions = {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'Poppins, sans-serif',
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '80%',
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: ['#03365D'],
        },
      },
      xaxis: {
        categories: labels,
        title: {
          text: 'TE',
          style: { color: '#03365D', fontWeight: 600 },
        },
        labels: {
          style: { colors: '#03365D' },
          rotate: -45,
          rotateAlways: true,
        },
      },
      yaxis: {
        title: {
          text: 'Session Count',
          style: { color: '#03365D', fontWeight: 600 },
        },
        labels: {
          style: { colors: '#03365D' },
          formatter: (val: number) => Math.round(val).toString(),
        },
      },
      colors: ['#03365D'],
      grid: {
        borderColor: '#e0e0e0',
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} session${val !== 1 ? 's' : ''}`,
        },
      },
    };

    const series = [{ name: 'Sessions', data: counts }];

    return { options, series };
  }, [sessionStatistics]);

  return <Chart options={chartData.options} series={chartData.series} type="bar" height={300} />;
}

/**
 * Pooled Differences Histogram Component
 * Shows distribution of all lap differences from session means
 */
function PooledDifferencesHistogram({
  lapDifferenceRecords,
}: {
  lapDifferenceRecords: LapDifferenceRecord[];
}): ReactElement {
  const chartData = useMemo(() => {
    const differences = lapDifferenceRecords.map((r) => r.difference);

    // Calculate bucket size based on data range
    // Use approximately 20 buckets across the data range
    const minDiff = Math.min(...differences);
    const maxDiff = Math.max(...differences);
    const range = maxDiff - minDiff;
    const bucketSize = range > 0 ? range / 20 : 0.001;

    // Create symmetric buckets around zero
    const maxAbsDiff = Math.max(Math.abs(minDiff), Math.abs(maxDiff));
    const numBucketsOneSide = Math.ceil(maxAbsDiff / bucketSize);

    const labels: string[] = [];
    const counts: number[] = [];

    // Create buckets from -max to +max
    for (let i = -numBucketsOneSide; i <= numBucketsOneSide; i++) {
      const start = i * bucketSize;
      labels.push(`${(start * 1000).toFixed(2)}`);
      counts.push(0);
    }

    // Count values in each bucket
    for (const diff of differences) {
      const bucketIndex = Math.floor((diff + numBucketsOneSide * bucketSize) / bucketSize);
      const clampedIndex = Math.max(0, Math.min(counts.length - 1, bucketIndex));
      counts[clampedIndex]++;
    }

    const options: ApexOptions = {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'Poppins, sans-serif',
      },
      plotOptions: {
        bar: {
          borderRadius: 2,
          columnWidth: '95%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: labels,
        title: {
          text: 'Difference from Session Mean (×10⁻³)',
          style: { color: '#03365D', fontWeight: 600 },
        },
        labels: {
          style: { colors: '#03365D', fontSize: '10px' },
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: true,
        },
        tickAmount: 10,
      },
      yaxis: {
        title: {
          text: 'Lap Count',
          style: { color: '#03365D', fontWeight: 600 },
        },
        labels: {
          style: { colors: '#03365D' },
          formatter: (val: number) => Math.round(val).toString(),
        },
      },
      colors: ['#6B7280'],
      grid: {
        borderColor: '#e0e0e0',
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} lap${val !== 1 ? 's' : ''}`,
        },
      },
    };

    const series = [{ name: 'Laps', data: counts }];

    return { options, series };
  }, [lapDifferenceRecords]);

  return <Chart options={chartData.options} series={chartData.series} type="bar" height={300} />;
}

const Statistics = (): ReactElement => {
  // State
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [sessionStatistics, setSessionStatistics] = useState<SessionStatistics[]>([]);
  const [aggregateStatistics, setAggregateStatistics] = useState<AggregateStatistics | null>(null);
  const [calculationMethod, setCalculationMethod] = useState<CalculationMethod>('mean');
  const [reportMethod, setReportMethod] = useState<CalculationMethod>('mean'); // Method used for current report
  const [lapDifferenceRecords, setLapDifferenceRecords] = useState<LapDifferenceRecord[]>([]);

  // Cache for loaded session data (persists until page refresh)
  const [sessionDataCache, setSessionDataCache] = useState<Map<string, any>>(new Map());

  // Export state
  const [isExportPreparing, setIsExportPreparing] = useState(false);
  const printContainerRef = useRef<HTMLDivElement>(null);

  // Hooks
  const { data: sessions, isPending: sessionsLoading } = useSessions();
  const { data: ridersById } = useRidersById();

  // Filter out singleton sessions and sort by date (newest first)
  const filteredSessions = useMemo(() => {
    return sessions
      ?.filter((session: any) => !SessionAnalysisTypeUtils.isSingletonLevel(session.analysis_type))
      ?.sort((a: any, b: any) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime());
  }, [sessions]);

  // Handlers
  const handleSelectAll = () => {
    if (selectedSessionIds.size === filteredSessions?.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(filteredSessions?.map((s: any) => s.id) || []));
    }
  };

  const handleSessionToggle = (sessionId: string) => {
    setSelectedSessionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleMethodChange = (method: CalculationMethod) => {
    setCalculationMethod(method);
    // Auto-generate report if sessions are selected
    if (selectedSessionIds.size > 0) {
      // Use setTimeout to ensure state is updated before generating
      setTimeout(() => generateReportWithMethod(method), 0);
    }
  };

  const generateReportWithMethod = async (method: CalculationMethod) => {
    setIsGenerating(true);
    setSessionStatistics([]);
    setAggregateStatistics(null);
    setLapDifferenceRecords([]);
    setProcessedCount(0);

    try {
      const selectedIds = Array.from(selectedSessionIds);
      setTotalToProcess(selectedIds.length);

      const stats: SessionStatistics[] = [];
      const allLapValuesBySession: number[][] = [];
      const allDifferenceRecords: LapDifferenceRecord[] = [];
      const newCacheEntries = new Map<string, any>();

      // Process sessions one at a time to show progress
      for (let i = 0; i < selectedIds.length; i++) {
        const sessionId = selectedIds[i];

        // Check cache first, fetch only if not cached
        let sessionData = sessionDataCache.get(sessionId);
        if (!sessionData) {
          sessionData = await getSessionCalcData(sessionId);
          newCacheEntries.set(sessionId, sessionData);
        }

        const riderName = ridersById?.get(sessionData.rider_id)?.nickname || 'Unknown';
        const sessionStat = calculateSessionStatistics(sessionData, riderName, method);

        if (sessionStat) {
          stats.push(sessionStat);

          // Extract lap values for pooled statistics calculation
          const traverseValues = extractConfigACdAValues(sessionData.configs || []);
          const lapValues = calculateLapCdAValues(traverseValues);
          allLapValuesBySession.push(lapValues);

          // Calculate mean and collect difference records for each lap
          const sessionMean = calculateMean(lapValues);
          for (const lapCdA of lapValues) {
            allDifferenceRecords.push({
              sessionDate: sessionStat.date,
              rider: riderName,
              sessionMeanCdA: sessionMean,
              lapCdA: lapCdA,
              difference: lapCdA - sessionMean,
            });
          }
        }
        setProcessedCount(i + 1);
      }

      // Update cache with newly fetched sessions
      if (newCacheEntries.size > 0) {
        setSessionDataCache((prev) => {
          const updated = new Map(prev);
          newCacheEntries.forEach((value, key) => updated.set(key, value));
          return updated;
        });
      }

      setSessionStatistics(stats);
      setAggregateStatistics(calculateAggregateStatistics(stats, allLapValuesBySession, method));
      setLapDifferenceRecords(allDifferenceRecords);
      setReportMethod(method); // Track which method was used for this report
    } catch (error) {
      console.error('Error generating statistics:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateReport = () => {
    generateReportWithMethod(calculationMethod);
  };

  const handleExport = async () => {
    setIsExportPreparing(true);

    // Allow React to render the spinner before starting export
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const dateStr = new Date().toISOString().split('T')[0];
    await executeExportHtml(printContainerRef, {
      waitTime: 3000,
      filename: `statistics_report_${dateStr}.html`,
      onAfterExport: () => setIsExportPreparing(false),
    });
  };

  const handleExportDifferencesCSV = () => {
    if (lapDifferenceRecords.length === 0) return;

    // Create CSV content
    const headers = ['Session Date', 'Rider', 'Session Mean CdAN_xG3', 'CdAN_xG3', 'Difference to Mean'];
    const rows = lapDifferenceRecords.map((record) => [
      record.sessionDate,
      record.rider,
      record.sessionMeanCdA.toFixed(6),
      record.lapCdA.toFixed(6),
      record.difference.toFixed(6),
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    link.download = `lap_differences_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Table column definitions
  const sessionColumns: ColumnDef<SessionStatistics>[] = useMemo(
    () => [
      { accessorKey: 'date', header: 'Date' },
      { accessorKey: 'rider', header: 'Rider' },
      { accessorKey: 'description', header: 'Description' },
      { accessorKey: 'totalRuns', header: 'Runs' },
      { accessorKey: 'totalLaps', header: 'Laps' },
      { accessorKey: 'totalTraverses', header: 'Traverses' },
      {
        accessorKey: 'meanCdA',
        header: 'Mean CdA',
        cell: ({ getValue }) => (getValue() as number).toFixed(4),
      },
      {
        accessorKey: 'stdDev',
        header: 'Std Dev',
        cell: ({ getValue }) => (getValue() as number).toFixed(4),
      },
      {
        accessorKey: 'cv',
        header: 'CV (%)',
        cell: ({ getValue }) => (getValue() as number).toFixed(2),
      },
      {
        accessorKey: 'te',
        header: 'TE',
        cell: ({ getValue }) => (getValue() as number).toFixed(4),
      },
      { accessorKey: 'timeSpan', header: 'Time Span' },
    ],
    []
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="cr-space-y-6 cr-p-6">
      <SectionHeader title="Statistics" isLoading={sessionsLoading || isGenerating || isExportPreparing} />

      {/* Results Section - Displayed FIRST when report is generated */}
      {sessionStatistics.length > 0 && (
        <div ref={printContainerRef} className="cr-space-y-8">
          {/* Export Button */}
          <div className="cr-flex cr-justify-end">
            <button
              onClick={handleExport}
              disabled={isExportPreparing}
              className="cr-flex cr-items-center cr-gap-2 cr-rounded-lg cr-bg-[#03365D] cr-px-4 cr-py-2 cr-font-main cr-text-[14px] cr-font-medium cr-text-white cr-transition-colors hover:cr-bg-[#1879B9] disabled:cr-cursor-not-allowed disabled:cr-opacity-50"
            >
              {isExportPreparing ? (
                <Spinner isFetching={true} />
              ) : (
                <HiOutlineDocumentArrowDown className="cr-h-5 cr-w-5" />
              )}
              {isExportPreparing ? 'Preparing Export...' : 'Export to PDF'}
            </button>
          </div>

          {/* Aggregate Statistics - FIRST in report */}
          {aggregateStatistics && (
            <div className="cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-p-6">
              <h2 className="font-montserrat cr-mb-6 cr-text-lg cr-font-semibold cr-text-[#03365D]">
                Aggregate Statistics for Baseline Repeats
                <span className="cr-ml-2 cr-text-sm cr-font-normal cr-text-gray-500">
                  ({reportMethod === 'sequential' ? 'Sequential' : 'Mean'} method)
                </span>
              </h2>
              <div
                className="cr-grid cr-grid-cols-2 cr-gap-6 md:cr-grid-cols-4"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}
              >
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Total Riders</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">{aggregateStatistics.totalRiders}</div>
                </div>
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Total Sessions</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">{aggregateStatistics.totalSessions}</div>
                </div>
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Total Runs</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">{aggregateStatistics.totalRuns}</div>
                </div>
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Total Laps</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">{aggregateStatistics.totalLaps}</div>
                </div>
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Mean CdA</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">
                    {aggregateStatistics.meanCdA.toFixed(4)}
                  </div>
                </div>
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Pooled Std Dev</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">
                    {aggregateStatistics.pooledStdDev.toFixed(4)}
                  </div>
                </div>
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Pooled CV (%)</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">
                    {aggregateStatistics.pooledCV.toFixed(2)}
                  </div>
                </div>
                <div className="cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-sm">
                  <div className="cr-text-sm cr-text-gray-500">Pooled TE</div>
                  <div className="cr-text-2xl cr-font-bold cr-text-[#03365D]">
                    {aggregateStatistics.pooledTE.toFixed(4)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Histograms - SECOND in report */}
          <div className="cr-grid cr-grid-cols-1 cr-gap-6 lg:cr-grid-cols-2">
            {/* CV Histogram */}
            <div className="cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-white cr-p-6">
              <h2 className="font-montserrat cr-mb-4 cr-text-lg cr-font-semibold cr-text-[#03365D]">
                CV Distribution (%)
              </h2>
              <CVHistogram sessionStatistics={sessionStatistics} />
            </div>

            {/* TE Histogram */}
            <div className="cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-white cr-p-6">
              <h2 className="font-montserrat cr-mb-4 cr-text-lg cr-font-semibold cr-text-[#03365D]">
                Typical Error (TE) Distribution
              </h2>
              <TEHistogram sessionStatistics={sessionStatistics} />
            </div>
          </div>

          {/* Per-Session Statistics Table - THIRD in report */}
          <div className="cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-white cr-p-6">
            <h2 className="font-montserrat cr-mb-4 cr-text-lg cr-font-semibold cr-text-[#03365D]">
              Per-Session Statistics (Config A)
              <span className="cr-ml-2 cr-text-sm cr-font-normal cr-text-gray-500">
                ({reportMethod === 'sequential' ? 'Sequential' : 'Mean'} method)
              </span>
            </h2>
            <Table data={sessionStatistics} columns={sessionColumns} />
          </div>

          {/* Pooled Differences Histogram - FOURTH in report */}
          {lapDifferenceRecords.length > 0 && (
            <div className="cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-white cr-p-6">
              <div className="cr-mb-4 cr-flex cr-items-center cr-justify-between">
                <h2 className="font-montserrat cr-text-lg cr-font-semibold cr-text-[#03365D]">
                  Pooled Differences from Session Mean
                  <span className="cr-ml-2 cr-text-sm cr-font-normal cr-text-gray-500">
                    ({lapDifferenceRecords.length} laps)
                  </span>
                </h2>
                <button
                  onClick={handleExportDifferencesCSV}
                  className="cr-flex cr-items-center cr-gap-2 cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-px-4 cr-py-2 cr-font-main cr-text-[14px] cr-font-medium cr-text-[#03365D] cr-transition-colors hover:cr-bg-cirrusBlue-50"
                >
                  <HiOutlineDocumentArrowDown className="cr-h-5 cr-w-5" />
                  Export Data (CSV)
                </button>
              </div>
              <PooledDifferencesHistogram lapDifferenceRecords={lapDifferenceRecords} />
            </div>
          )}
        </div>
      )}

      {/* Session Selection Section - Displayed AFTER report (or first if no report) */}
      <div className="cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-white cr-p-6">
        <div className="cr-mb-4 cr-flex cr-items-center cr-justify-between">
          <h2 className="font-montserrat cr-text-lg cr-font-semibold cr-text-[#03365D]">Select Sessions</h2>
          <div className="cr-flex cr-items-center cr-gap-6">
            {/* Calculation Method Radio Buttons */}
            <div className="cr-flex cr-items-center cr-gap-4">
              <span className="cr-text-sm cr-font-medium cr-text-[#03365D]">Method:</span>
              <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
                <input
                  type="radio"
                  name="calculationMethod"
                  value="mean"
                  checked={calculationMethod === 'mean'}
                  onChange={() => handleMethodChange('mean')}
                  disabled={isGenerating}
                  className="cr-h-4 cr-w-4 cr-border-gray-300 cr-text-[#03365D] focus:cr-ring-[#03365D]"
                />
                <span className="cr-text-sm cr-text-gray-700">Mean</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
                <input
                  type="radio"
                  name="calculationMethod"
                  value="sequential"
                  checked={calculationMethod === 'sequential'}
                  onChange={() => handleMethodChange('sequential')}
                  disabled={isGenerating}
                  className="cr-h-4 cr-w-4 cr-border-gray-300 cr-text-[#03365D] focus:cr-ring-[#03365D]"
                />
                <span className="cr-text-sm cr-text-gray-700">Sequential</span>
              </label>
            </div>

            <button
              onClick={handleSelectAll}
              className="cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-px-4 cr-py-2 cr-font-main cr-text-[14px] cr-font-medium cr-text-[#03365D] cr-transition-colors hover:cr-bg-cirrusBlue-50"
            >
              {selectedSessionIds.size === filteredSessions?.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={handleGenerateReport}
              disabled={isGenerating || selectedSessionIds.size === 0}
              className="cr-flex cr-items-center cr-gap-2 cr-rounded-lg cr-bg-[#03365D] cr-px-4 cr-py-2
                cr-font-main cr-text-[14px] cr-font-medium cr-text-white cr-transition-colors
                hover:cr-bg-[#1879B9] disabled:cr-cursor-not-allowed disabled:cr-opacity-50"
            >
              {isGenerating && <Spinner isFetching={true} />}
              {isGenerating ? `Processing ${processedCount} of ${totalToProcess}...` : 'Generate Report'}
            </button>
          </div>
        </div>

        <div className="cr-text-sm cr-text-gray-500 cr-mb-4">
          {selectedSessionIds.size} of {filteredSessions?.length || 0} sessions selected
        </div>

        {/* Session List */}
        <div className="cr-max-h-[600px] cr-overflow-y-auto cr-rounded-lg cr-border cr-border-gray-200">
          {filteredSessions?.map((session: any) => (
            <div
              key={session.id}
              className={`cr-flex cr-cursor-pointer cr-items-center cr-gap-3 cr-border-b
                cr-border-gray-100 cr-px-3 cr-py-2 cr-transition-colors last:cr-border-b-0
                hover:cr-bg-cirrusBlue-25 ${
                selectedSessionIds.has(session.id) ? 'cr-bg-cirrusBlue-50' : 'cr-bg-white'
              }`}
              onClick={() => handleSessionToggle(session.id)}
            >
              <input
                type="checkbox"
                checked={selectedSessionIds.has(session.id)}
                onChange={() => handleSessionToggle(session.id)}
                onClick={(e) => e.stopPropagation()}
                className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-[#03365D] focus:cr-ring-[#03365D]"
              />
              <span className="cr-font-semibold cr-text-[#03365D] cr-min-w-[100px]">{formatDate(session.date)}</span>
              <span className="cr-text-gray-400">|</span>
              <span className="cr-text-gray-600 cr-min-w-[120px]">
                {ridersById?.get(session.rider_id)?.nickname || 'Unknown Rider'}
              </span>
              <span className="cr-text-gray-400">|</span>
              <span className="cr-text-gray-500 cr-flex-1 cr-truncate">{session.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Empty State */}
      {!isGenerating && sessionStatistics.length === 0 && selectedSessionIds.size > 0 && (
        <div className="cr-rounded-lg cr-border-2 cr-border-dashed cr-border-gray-300 cr-p-8 cr-text-center">
          <p className="cr-text-gray-500">
            Click &quot;Generate Report&quot; to calculate statistics for selected sessions.
          </p>
        </div>
      )}
    </div>
  );
};

export default Statistics;
